import { useCallback, useEffect, useState } from "react";
import {
  clearNetworkQualityCache,
  getCachedNetworkQuality,
  NetworkQualityResult,
  probeNetworkQuality,
  subscribeToNetworkInformationChanges,
  VIDEO_MODE_DISABLED_MESSAGE,
} from "../utils/networkQuality";

const DEFAULT_QUALITY: NetworkQualityResult = {
  isVideoModeAvailable: true,
  rttMs: null,
  throughputMbps: null,
};

export const useVideoModeAvailability = (active: boolean) => {
  const [quality, setQuality] = useState<NetworkQualityResult>(DEFAULT_QUALITY);
  const [isCheckingNetwork, setIsCheckingNetwork] = useState(false);
  const [isOffline, setIsOffline] = useState(
    () => typeof navigator !== "undefined" && navigator.onLine === false,
  );

  useEffect(() => {
    const syncOfflineState = () => {
      setIsOffline(navigator.onLine === false);
    };

    window.addEventListener("online", syncOfflineState);
    window.addEventListener("offline", syncOfflineState);
    return () => {
      window.removeEventListener("online", syncOfflineState);
      window.removeEventListener("offline", syncOfflineState);
    };
  }, []);

  const refreshNetworkQuality = useCallback(async () => {
    if (navigator.onLine === false) {
      setIsCheckingNetwork(false);
      setQuality(DEFAULT_QUALITY);
      return;
    }

    const cached = getCachedNetworkQuality();
    if (cached) {
      setQuality(cached);
      setIsCheckingNetwork(false);
      return;
    }

    setIsCheckingNetwork(true);

    try {
      const result = await probeNetworkQuality({
        bypassCache: true,
        onProgress: (partial) => {
          setQuality(partial);
          if (!partial.isVideoModeAvailable) {
            setIsCheckingNetwork(false);
          }
        },
      });
      setQuality(result);
    } finally {
      setIsCheckingNetwork(false);
    }
  }, []);

  useEffect(() => {
    if (!active) return;

    clearNetworkQualityCache();
    setQuality(DEFAULT_QUALITY);
    setIsCheckingNetwork(navigator.onLine !== false);
    void refreshNetworkQuality();

    const unsubscribe = subscribeToNetworkInformationChanges(() => {
      clearNetworkQualityCache();
      if (navigator.onLine !== false) {
        setIsCheckingNetwork(true);
      }
      void refreshNetworkQuality();
    });

    return unsubscribe;
  }, [active, refreshNetworkQuality]);

  // Offline is not slow network: keep Video clickable so Go can show the offline modal.
  const isVideoModeAvailable = isOffline
    ? true
    : quality.isVideoModeAvailable && !isCheckingNetwork;
  const videoDisabledReason = isOffline
    ? undefined
    : isCheckingNetwork
      ? "Checking connection..."
      : (quality.reason ??
        (!isVideoModeAvailable ? VIDEO_MODE_DISABLED_MESSAGE : undefined));

  return {
    isVideoModeAvailable,
    isCheckingNetwork: isOffline ? false : isCheckingNetwork,
    videoDisabledReason,
    refreshNetworkQuality,
  };
};
